/**
 * **Sur quoi le bouton « Vérifier » porte réellement — et comment l'écran le dit.**
 *
 * ## Le défaut
 *
 * `activities.check` et `contacts.review` (`@ndrc/core-domain`) relisent **les fiches clients de
 * l'espace de l'élève**, et rien d'autre. Le bouton, lui, s'affichait sur TOUTE activité et
 * s'appelait « Vérifier mon travail ». Le 8 septembre 2026, une consigne portant sur l'entretien
 * téléphonique — outil « Appel », qui ne touche à aucune fiche — proposait donc ce bouton ; l'élève
 * cliquait et recevait un compte rendu sur seize fiches clients sans rapport avec ce qu'on lui
 * avait demandé, sans aucun moyen de s'en apercevoir.
 *
 * Un contrôle qui a l'air d'en être un et n'en est pas est pire que pas de contrôle. Il n'y a
 * pourtant pas de seconde relecture à construire : il y a une relecture à rendre **honnête**.
 *
 * ## La règle, et pourquoi elle ne nomme aucune app
 *
 * Un manifeste déclare les entités du cœur que son app utilise (`entities.uses`,
 * `@ndrc/app-sdk/manifest`). La question « cette relecture a-t-elle un rapport avec le travail du
 * jour ? » se répond donc sans jamais comparer un identifiant d'app (règle 1 de `CLAUDE.md`) :
 * l'outil désigné par la consigne déclare-t-il `contacts` ?
 *
 * Une app installée demain qui travaille les fiches clients sera couverte sans qu'une ligne d'ici
 * ne bouge ; une qui ne les touche pas déclenchera l'avertissement sans qu'on ait eu à penser à
 * elle. C'est le §52 du cahier des charges appliqué à une phrase d'écran.
 *
 * ## Ce que la fonction ne fait PAS
 *
 * Elle ne masque pas le bouton, et ne le désactive pas. Un élève peut avoir une bonne raison de
 * relire ses fiches pendant une séance d'appels — il a le droit de le faire, il doit simplement
 * savoir ce qu'il relit. Cacher le contrôle aurait remplacé un mensonge par une porte fermée.
 */

/** L'entité du cœur que la relecture lit réellement. Une seule, et c'est le sujet du problème :
 * `activities.check` interroge `core.contacts`, `contacts.review` aussi. */
export const REVIEWED_ENTITY = 'contacts';

/**
 * Le rapport entre la relecture et l'outil de la consigne du jour.
 *
 * - `covered` — l'outil travaille les fiches clients : la relecture porte bien sur le travail
 *   demandé, rien à signaler.
 * - `offSubject` — l'outil ne les touche pas. C'est LE cas du défaut : à dire à l'écran.
 * - `noTool` — la consigne ne désigne aucun outil. On ne peut rien affirmer sur son sujet ; on dit
 *   donc ce que la relecture regarde, sans accuser.
 * - `unknownTool` — l'outil a été désinstallé depuis. On ne sait plus ce qu'il touchait : le
 *   silence laisserait croire que la relecture porte sur le travail du jour.
 */
export type ToolReviewFit = 'covered' | 'offSubject' | 'noTool' | 'unknownTool';

/** Le strict nécessaire d'un manifeste pour répondre. Structurel, jamais `AppManifest` entier :
 * `DeskManifest` (`host/registry.ts`) est déjà ce sous-ensemble, et cette fonction se teste avec
 * des manifestes fabriqués à la main. */
export interface ReviewedToolManifest {
  entities: { uses: readonly { entity: string }[] };
}

export function toolReviewFit(
  appId: string | null,
  manifest: ReviewedToolManifest | undefined,
): ToolReviewFit {
  if (appId === null) return 'noTool';
  if (manifest === undefined) return 'unknownTool';
  return manifest.entities.uses.some((use) => use.entity === REVIEWED_ENTITY)
    ? 'covered'
    : 'offSubject';
}
